export {
  DEFAULT_TAG_BG_COLOR,
  TAILWIND_TAG_BG_CLASSES,
} from "@/constants/tailwind-tag-palette";

export const API_ENDPOINTS = {
  ASSISTANTS: "/api/assistants?custom_only=true",
  CONVERSATIONS: "/api/conversations",
  NEW_CONVERSATION: "/api/conversations/new",
  CHAT_ABANDON_CYCLE: "/api/conversations/chat/abandon-cycle",
  RESUME_STREAM: (conversationId: string, messageId: string) =>
    `/api/conversations/${encodeURIComponent(conversationId)}/resume-stream?messageId=${encodeURIComponent(messageId)}`,
  SEND_MESSAGE: (conversationId: string) =>
    `/api/conversations/messages/${encodeURIComponent(conversationId)}`,
  CONVERSATION_MESSAGE_FEEDBACKS: (conversationId: string, messageId: string) =>
    `/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedbacks`,
  CONVERSATION_FEEDBACKS: (conversationId: string) =>
    `/api/conversations/${encodeURIComponent(conversationId)}/feedbacks`,

  // SidonIAssist API endpoints (canonical routes)

  // Reference tables
  DOCUMENT_TYPES: "/api/ref/document-types",
  DEFAULT_USER_TAGS: "/api/ref/default-user-tags",
  /** Utilisateur DB courant (cookie de session Sidoni, même domaine que l’assist) */
  CURRENT_USER: "/api/users/me",
  USER_QUOTA_ME: "/api/users/me/quota",
  /** Annonce de service globale (ADR dust-api 0029) */
  SERVICE_ANNOUNCEMENT: "/api/service-announcement",
  // Conversations (DB operations)
  CONVERSATIONS_DB: "/api/conversations/db",
  CONVERSATION_QUESTIONS_PATCH: (dustConversationId: string) =>
    `/api/conversations/db/by-dust/${encodeURIComponent(dustConversationId)}/questions`,
  CONVERSATION_QUESTIONS_BACKFILL_CHUNK:
    "/api/conversations/db/questions/backfill-chunk",
  CONVERSATION_GATEKEEPER_PRECISIONS: (dustConversationId: string) =>
    `/api/conversations/db/gatekeeper-precisions/by-dust/${encodeURIComponent(dustConversationId)}`,
  CONVERSATION: (id: number) => `/api/conversations/db/${id}`,
  CONVERSATION_LABELS: (id: number) => `/api/conversations/db/${id}/labels`,
  CONVERSATION_LABEL: (id: number, labelType: string, tagId: string) =>
    `/api/conversations/db/${id}/labels/${encodeURIComponent(labelType)}/${encodeURIComponent(tagId)}`,
  CONVERSATION_SHARE: (id: number) => `/api/conversations/db/${id}/share`,
  // Route publique (sans auth) — lien de partage
  SHARED_CONVERSATION: (token: string) =>
    `/api/share/${encodeURIComponent(token)}`,
  // User tags
  USER_TAGS_ME: "/api/users/me/tags",
  USER_TAG_ME: (tagId: string) =>
    `/api/users/me/tags/${encodeURIComponent(tagId)}`,
  /** Acceptation des CGU (version validée côté serveur) */
  USER_CGU_ACCEPTANCE: "/api/users/me/cgu",
  /** Configuration CGU (URLs + version requise), alignée sur `GET /users/me` */
  CGU_CONFIG: "/api/cgu",
  /** Flags auth publics (gate bêta, etc.) */
  AUTH_CONFIG: "/api/auth/config",
  // Sidoni documents enrichment (authenticated)
  SIDONI_DOCUMENTS_BY_IDS: "/api/sidoni-documents/by-ids",
  SIDONI_DOCUMENTS_BY_NOMS: "/api/sidoni-documents/by-noms",
  SIDONI_DOCUMENTS_BY_PJ_NOMS: "/api/sidoni-documents/by-pj-noms",
  // Sidoni documents enrichment — public endpoints (validated by share token)
  SHARE_DOCUMENTS_BY_IDS: (token: string) =>
    `/api/share-documents/${encodeURIComponent(token)}/by-ids`,
  SHARE_DOCUMENTS_BY_NOMS: (token: string) =>
    `/api/share-documents/${encodeURIComponent(token)}/by-noms`,
  SHARE_DOCUMENTS_BY_PJ_NOMS: (token: string) =>
    `/api/share-documents/${encodeURIComponent(token)}/by-pj-noms`,
} as const;

/**
 * Clé stable pour TanStack Query / collections liées à l’utilisateur authentifié
 * (l’identité réelle vient des cookies de session, pas de cet identifiant).
 */
export const SESSION_SCOPED_USER_QUERY_KEY = "me" as const;

/**
 * TanStack DB `where()` must use query helpers (`eq`, …), not raw JS booleans.
 * Use with `eq(conversationId, …)` when no conversation is selected so no real row matches.
 */
export const NO_ACTIVE_CONVERSATION_QUERY_KEY =
  "__sidoni_internal_no_active_conversation__";

/**
 * Client-only sentinel while a new conversation stream is in flight but Dust
 * has not yet emitted `conversation_created` (e.g. gatekeeper score 0/1).
 * Never persist, hydrate, or call Dust APIs with this value.
 */
export const PENDING_NEW_CONVERSATION_SENTINEL = "pending-new-conversation";

/** Max length for message feedback comment (aligned with dust-api DB/API). */
export const MESSAGE_FEEDBACK_CONTENT_MAX_LENGTH = 2000;

/** Composer principal and its expanded editor. */
export const COMPOSER_MESSAGE_MAX_LENGTH = 32_000;

/** Show the compact composer counter once fewer than 10 % of the ceiling remain. */
export const COMPOSER_REMAINING_COUNT_REVEAL_BELOW = 3_200;

/** Max undo entries for the shared composer / message-editor draft journal. */
export const COMPOSER_EDIT_HISTORY_MAX_DEPTH = 200;

/** Merge consecutive typing into one undo step while keystrokes stay this close. */
export const COMPOSER_EDIT_HISTORY_COALESCE_MS = 400;

/** Réponse libre de secours and each « Autre » field. */
export const PRECISION_CUSTOM_RESPONSE_MAX_LENGTH = 4_096;

// Avertissement affiché systématiquement dans les réponses structurées
// concernant la re-codification du code de commerce (ordonnance du 6/12/2023)
export const AVERTISSEMENT_RECODIFICATION = `Le Code de Commerce a fait l'objet d'une recodification importante en 2023-2024 (ordonnance n°2023-1142 du 6 décembre 2023, décret n°2023-1394 du 30 décembre 2023, arrêtés du 28 décembre 2023).\n\n Les documents SIDONI publiés avant le 1er janvier 2024 conservent leur pertinence doctrinale, mais les références aux articles du Code de Commerce peuvent avoir évolué.`;

/** Interface d'administration SidonIAssist (même origine que l'assist) */
export const ADMIN_URL = "/admin" as const;

// Titre de la section "Fiches pratiques SidonIAssist" sur la page d'accueil
export const HOME_FICHES_PRATIQUES_SECTION_TITLE =
  "Fiches de la CNCC sur les bonnes pratiques IA & Audit";

export const HOME_CHARTE_IA_LINKS = [
  {
    id: "bonnes-pratiques",
    label: "Fiche IA & Audit\nBonnes pratiques",
    icon: "BookOpen",
    path: "docs/fiches-bonnes-pratiques-ia-intro2026",
  },
  {
    id: "bonnes-pratiques-avance",
    label: "Fiche IA & Audit\nBonnes pratiques\n(Niveau avancé)",
    icon: "GraduationCap",
    path: "docs/fiches-bonnes-pratiques-ia-niveaa2026",
  },
  {
    id: "charte-cabinet",
    label: "Exemple de Charte IA pour mon cabinet",
    icon: "ScrollText",
    path: "docs/charte-ia/outils-lies",
  },
] as const;

// Titre de la section "Exemples de questions pour l'assistant SidonIAssist" sur la page d'accueil
export const HOME_SUGGESTIONS_SECTION_TITLE =
  "Exemples de questions pour l'assistant SidonIAssist";

// **Exemples de questions** (carousel accueil): sauts de ligne uniquement via `\n`
// explicites dans une seule string. Pas de template multiligne, pas de dedent, pas de join.
// Prose: `\n\n` entre chaque phrase / bloc logique. Listes: `\n` entre items ;
// `\n\n` avant/après la liste. Une phrase courte reste sur une seule ligne.
export const HOME_SUGGESTIONS = [
  {
    id: "documents-ajoutes-modifies-sidoni",
    label:
      "Liste moi les documents ajoutés ou modifiés dans SIDONI durant ces 30 derniers jours",
    icon: "Calendar",
  },
  {
    id: "difference-nep-315r",
    label:
      "Je n'arrive pas à saisir la différence entre la NEP 315R et l'ancienne version.\n\nQu'est-ce qui change concrètement ?",
    icon: "GitCompare",
  },
  {
    id: "nullite-rapport-commissaire",
    label:
      "Le défaut de présentation du rapport du commissaire aux comptes sur les comptes annuels ou consolidés à l’assemblée générale dans les SA et SCA entraine t il la nullité des délibérations de l’assemblée générale ?",
    icon: "FileWarning",
  },
  {
    id: "non-depot-comptes-greffe",
    label:
      "Est-ce que le non dépôt des comptes au Greffe est une infraction pénale, et selon quel article du code de commerce stp?",
    icon: "CircleAlert",
  },
  {
    id: "arret-comptes-sasu-redressement-judiciaire",
    label:
      "Je suis commissaire aux comptes d'une SASU qui clôture ses comptes annuels au 31/12/2025 et est en redressement judiciaire depuis le 26/11/2025.\n\nLa mission d'assistance de de l'administrateur judiciaire a été convertie en mission de représentation par jugement du Tribunal de commerce du 11/02/2026.\n\nLes statuts ne prévoient rien concernant l'arrêté des comptes annuels.\n\nLes statuts prévoient que les décisions de l'associé unique doivent être prises par acte sous seing privé qui doit indiquer les documents et rapports soumis à l'associé unique et les résolutions adoptées.\n\nLes statuts prévoient également que le commissaire aux comptes doit être préalablement informé des décisions de l'associé unique.\n\nDans ce contexte, quelles sont les règles de fonds et de forme à respecter pour arrêter les comptes annuels 31/12/2025 ?",
    icon: "Scale",
  },
  {
    id: "erreur-changement-comptable-reglement-anc",
    label:
      "Existe-t-il une doctrine CNCC où une entité n'a pas appliqué (omission, erreur ou mauvaise application) un changement comptable requis par un nouveau règlement ANC ?\n\nMais l'anomalie a été découvert lors de l'exercice N+1 ?\n\nComment aborder cette erreur ?\n\ncomptes individuels (sociaux) exercice ouvert avant le 01/01/2025",
    icon: "RefreshCw",
  },
  {
    id: "comptabilisation-indemnite-assurance-incendie",
    label:
      "Fait générateur de la comptabilisation d'une indemnité d'assurance non encore versée à la suite de l'incendie d'une immobilisation ?\n\nL'exercice est ouvert (a) avant le 01/01/2025 [ANC 2014-03].\n\nLa question porte sur des comptes individuels (sociaux).\n\nL'entité est une entreprise commerciale",
    icon: "Flame",
  },
  {
    id: "documents-transformation-sci-sas",
    label:
      "Liste des documents à obtenir pour une transformation d'une SCI en SAS ?",
    icon: "FolderOpen",
  },
  {
    id: "transfert-mandats-cac-apa",
    label:
      "Lorsqu’un cabinet de CAC réalise une APA (Apport partiel d’actif (APA), ou un Contrat d’Achat d’Actifs d’un autre cabinet de CAC, est-ce que les mandats de certifications continuent sous le nom du nouveau cabinet, ou faut-il nommer le nouveau commissaire au compte en AG ?\n\nAPA sous régime TUP.\n\nLes mandats concernés portent sur des sociétés commerciales",
    icon: "ArrowRightLeft",
  },
  {
    id: "mandat-alpe-marche-non-reglemente",
    label:
      "Pourrais-tu également m'indiquer si un mandat ALPE peut être réalisé sur une société cotée sur un marché non réglementé ?",
    icon: "Building2",
  },
  {
    id: "audit-situation-intermediaire-sas",
    label:
      "Je recherche de la documentation et modèle de lettre de mission et rapport pour effectuer un audit sur une situation intermédiaire au 31/05 pour un client SAS non EIP dont je suis CAC et qui habituellement clôture au 31/12.\n\nJ'interviens en qualité de CAC titulaire d'un mandat.\n\nL'exercice en cours a-t-il été ouvert à compter du 01/01/2025 (ANC 2022-06) ?",
    icon: "ClipboardList",
  },
  {
    id: "cac-transformation-sarl-sas",
    label:
      "Je suis CAC titulaire d'une SARL qui souhaite se transformer SAS.\n\nEst-ce que je peux accepter la mission de CAC à la transformation ?\n\nIl s'agit d'une SARL non EIP qui ne fait pas partie d'un groupe de sociétés ?",
    icon: "FileText",
  },
  {
    id: "duree-mission-alpe-sas",
    label:
      "Quelle est la durée d'une mission ALPE sachant que forme juridique de l'entité est une SAS qui n'appartient pas à un groupe ?",
    icon: "Clock",
  },
  {
    id: "diligences-cac-fusions-opci",
    label:
      "Quelles sont les diligences du commissaire aux comptes lors des fusions d'OPCI ?",
    icon: "ListChecks",
  },
  {
    id: "recuperation-notes-information-outils-sidoni",
    label:
      "Je veux récupérer les documents suivant :\n\nNI XV + FAQ « NEP 315R et NEP 330R » ;\nNI XXI + annexe 1 ;\nNI XXII ;\nNI VIII ;\nNI VII + réponse CNP « Questions fréquentes : demandes de confirmation des tiers » (nov. 2024) ;\nNI X + communiqué CNCC sur le PCG modernisé (oct. 2025, public) ;\nNI IV + exemple de lettre d'affirmation French GAAP (mars 2026, public) ;\nNI II ;\nNI III + listes d'outils bureautiques (2022) ;\nNI XVIII + questionnaire VIS SA non cotées ;\nNI IX + modèle P1 (2020) et liste d'outils ;\nNI I + exemples de rapports non EIP (fév. 2026 : E1-1-1, E1-1-3, E2, E3, E4) ;\nNEP 912 révisée (juil. 2026, public).\n\nDeux autres documents sont à votre choix, en plus : le questionnaire de contrôle de l'annexe (janv. 2026) et le questionnaire de fin de mission (oct. 2024). Ils serviraient pour le contrôle de l'annexe (onglet D-4) et pour la fin de mission.",
    icon: "Download",
  },
] as const;

// Liens complémentaires de la page d'accueil (href doc résolu via auth/config)
export const HOME_FOOTER_LINKS = [
  { label: "CGU", href: "#cgu", kind: "hash" as const },
  {
    label: "Documentation Sidoni",
    href: "doc-root",
    kind: "sidoni-doc" as const,
  },
  {
    label: "Mentions légales",
    href: "#mentions-legales",
    kind: "hash" as const,
  },
] as const;
